import { FC, useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import useSignupContext from '@hooks/context/useSignupContext';
import { useFormContext, useWatch } from 'react-hook-form';
import { EAddressType } from '@xeppt/xeppt-sdk/types/user';
import { getAddressFormName, getAddressName } from '@utils/index';
import Radio from '@components/common/radio';
import { defaultAddressData } from '@const/default_form_data';
import FullAddress from '@components/address';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { apiRegistrationService } from '@api';
import { TRegisterBusinessUser } from '@xeppt/xeppt-sdk/types';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { Country } from 'country-state-city';
import { useApiMutation } from '@hooks/api/useApiMutation';
import moment from 'moment-timezone';
import addressIllustration from '@svg/illustrations/auth/address.svg';

interface IProps {
    type: EAddressType;
    onChange: (val: EAddressType) => void;
}

const BusinessAddressSection: FC<IProps> = ({ type, onChange }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'signup.business_address'
    });
    const { labelLocale, submitLocale, requestSuccessLocale, requestErrorLocale } = useLocales();
    const [isHidden, setIsHidden] = useState(false);
    const { nextStep, prevStep, handleChangeIllustration } = useSignupContext();
    const {
        control,
        formState: { errors },
        setValue,
        getValues
    } = useFormContext();
    const { handleRequest: onCreate, isLoading } = useApiMutation({
        method: () => {
            const values = getValues();
            return apiRegistrationService.registerBusiness({
                ...values,
                profile: {
                    ...values.profile,
                    timezone: moment.tz.guess()
                },
                trading_address: {
                    ...values.trading_address,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === values.trading_address.country
                        )?.isoCode || values.trading_address.country
                },
                business_address: {
                    ...values.business_address,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === values.business_address.country
                        )?.isoCode || values.business_address.country
                },
                registration_address: {
                    ...values.registration_address,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === values.registration_address.country
                        )?.isoCode || values.registration_address.country
                },
                billing_address: {
                    ...values.billing_address,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === values.billing_address.country
                        )?.isoCode || values.billing_address.country
                }
            } as TRegisterBusinessUser);
        },
        onSuccess: () => {
            requestSuccessLocale('registration');
            nextStep();
        },
        onError: requestErrorLocale
    });

    useEffect(() => {
        handleChangeIllustration(addressIllustration);
    }, []);

    const onSubmit = () => {
        if (type === EAddressType.BUSINESS) {
            onChange(EAddressType.REGISTRATION);
        } else if (type === EAddressType.REGISTRATION) {
            onChange(EAddressType.BILLING);
        } else if (type === EAddressType.BILLING) {
            onChange(EAddressType.TRADING);
        } else {
            onCreate(undefined);
        }
    };

    const onBack = () => {
        if (type === EAddressType.BUSINESS) {
            prevStep();
        } else if (type === EAddressType.REGISTRATION) {
            onChange(EAddressType.BUSINESS);
        } else if (type === EAddressType.BILLING) {
            onChange(EAddressType.REGISTRATION);
        } else {
            onChange(EAddressType.BILLING);
        }
    };

    const business_address = useWatch({ name: 'business_address', control });
    const registration_address = useWatch({
        name: 'registration_address',
        control
    });
    const trading_address = useWatch({ name: 'trading_address', control });
    const billing_address = useWatch({ name: 'billing_address', control });

    const registrationSameBusiness = useWatch({
        name: 'registrationSameBusiness',
        control
    });
    const tradingSameBusiness = useWatch({
        name: 'tradingSameBusiness',
        control
    });
    const billingSameBusiness = useWatch({
        name: 'billingSameBusiness',
        control
    });

    console.log(getValues());

    const getAddressValue = () => {
        if (type === EAddressType.BUSINESS) {
            return business_address;
        } else if (type === EAddressType.REGISTRATION) {
            return registration_address;
        } else if (type === EAddressType.BILLING) {
            return billing_address;
        } else {
            return trading_address;
        }
    };
    const getIsSameValue = () => {
        if (type === EAddressType.REGISTRATION) {
            return registrationSameBusiness;
        } else if (type === EAddressType.TRADING) {
            return tradingSameBusiness;
        } else if (type === EAddressType.BILLING) {
            return billingSameBusiness;
        }
    };
    const handleChangeIsSame = (value: boolean) => {
        setIsHidden(true);
        setTimeout(() => {
            setIsHidden(false);
        }, 0);
        if (type === EAddressType.REGISTRATION) {
            if (value) {
                setValue('registration_address', business_address);
            } else {
                setValue('registration_address', defaultAddressData);
            }
            setValue('registrationSameBusiness', value);
        } else if (type === EAddressType.TRADING) {
            if (value) {
                setValue('trading_address', business_address);
            } else {
                setValue('trading_address', defaultAddressData);
            }
            setValue('tradingSameBusiness', value);
        } else if (type === EAddressType.BILLING) {
            if (value) {
                setValue('billing_address', business_address);
            } else {
                setValue('billing_address', defaultAddressData);
            }
            setValue('billingSameBusiness', value);
        }
    };
    const getCurrentStep = () => {
        if (type === EAddressType.BUSINESS) {
            return '8';
        } else if (type === EAddressType.REGISTRATION) {
            return '9';
        } else if (type === EAddressType.BILLING) {
            return '10';
        }
        return '11';
    };

    useEffect(() => {
        setIsHidden(true);
        setTimeout(() => {
            setIsHidden(false);
        }, 0);
    }, [type]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.steps}>STEP {getCurrentStep()}/11</div>
            <Typography
                className={type === EAddressType.BUSINESS ? styles.description : styles.title}
                variant="h3">
                {t('title')} {getAddressName(type, t)}
            </Typography>
            {type !== EAddressType.BUSINESS && (
                <Typography className={styles.description} variant="body2">
                    {t('description_1')} {getAddressName(type, t)} {t('description_2')}
                </Typography>
            )}
            {type !== EAddressType.BUSINESS && (
                <>
                    <div className={styles.radio_row}>
                        <Typography variant="body3">
                            {t('same_business_1')} {getAddressName(type, t)} {t('same_business_2')}
                        </Typography>
                        <Radio
                            checked={getIsSameValue()}
                            small
                            onClick={() => handleChangeIsSame(true)}
                        />
                    </div>
                    {getIsSameValue() && (
                        <div className={styles.same_address}>
                            <Typography variant="body3">
                                <b>{labelLocale('country')}:</b> {business_address?.country}
                            </Typography>
                            <Typography variant="body3">
                                <b>{labelLocale('city')}:</b> {business_address?.city}
                            </Typography>
                            <Typography variant="body3">
                                <b>{labelLocale('address_1')}:</b> {business_address?.address_1}
                            </Typography>
                            <Typography variant="body3">
                                <b>{labelLocale('address_2')}:</b>{' '}
                                {business_address?.streetName || '-'}
                            </Typography>
                            <Typography variant="body3">
                                <b>{labelLocale('region')}:</b> {business_address?.region}
                            </Typography>
                            <Typography variant="body3">
                                <b>{labelLocale('zip_code')}:</b> {business_address?.zip_code}
                            </Typography>
                        </div>
                    )}
                    <div className={styles.radio_row}>
                        <Typography variant="body3">
                            {t('add_another')} {getAddressName(type, t)}
                        </Typography>
                        <Radio
                            small
                            checked={!getIsSameValue()}
                            onClick={() => handleChangeIsSame(false)}
                        />
                    </div>
                </>
            )}
            <div className={styles.form}>
                <FullAddress
                    hidden={type !== EAddressType.BUSINESS || isHidden}
                    name={getAddressFormName(EAddressType.BUSINESS)}
                />
                <FullAddress
                    hidden={
                        type !== EAddressType.REGISTRATION || isHidden || registrationSameBusiness
                    }
                    name={getAddressFormName(EAddressType.REGISTRATION)}
                />
                <FullAddress
                    hidden={type !== EAddressType.TRADING || isHidden || tradingSameBusiness}
                    name={getAddressFormName(EAddressType.TRADING)}
                />
                <FullAddress
                    hidden={type !== EAddressType.BILLING || isHidden || billingSameBusiness}
                    name={getAddressFormName(EAddressType.BILLING)}
                />
                <div className={styles.actions}>
                    <Button
                        variant="outlined-primary"
                        className={styles.back}
                        leftIcon="arrow_left"
                        onClick={onBack}>
                        {submitLocale('back')}
                    </Button>
                    <Button
                        disabled={Object.keys(errors)?.length !== 0 || false}
                        className={styles.next}
                        onClick={onSubmit}
                        rightIcon="arrow_right"
                        isLoading={isLoading}
                        variant="primary">
                        {submitLocale('next')}
                    </Button>
                </div>
            </div>
        </motion.div>
    );
};

export default BusinessAddressSection;
